import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { PageContainer } from "../shared/components/PageContainer";
import { useFetchContext } from "../../contexts/FetchContext";
import { useAuthContext } from "../../contexts/AuthContext";

interface InventoryItem {
  productCode: string;
  quantity: number;
  pendingOrders?: number;
}

export const Relocate = () => {
  const { fetchData } = useFetchContext();
  const { userData } = useAuthContext();
  const warehouseId = userData?.user?.warehouseId;

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<InventoryItem[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selected, setSelected] = useState<InventoryItem[]>([]);
  const [location, setLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Ricerca server-side sui prodotti in giacenza (debounce ~300ms)
  useEffect(() => {
    if (!warehouseId) return;
    let active = true;
    setLoadingOptions(true);
    const handler = setTimeout(async () => {
      try {
        const response = await fetchData(
          `/products/inventory-check?warehouseId=${warehouseId}&page=1&pageSize=20&orderBy=productCode&orderType=ASC${
            inputValue ? `&searchKeyword=${encodeURIComponent(inputValue)}` : ""
          }`
        );
        if (active) setOptions(response?.data?.items ?? []);
      } catch (error) {
        if (active) setOptions([]);
      } finally {
        if (active) setLoadingOptions(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, warehouseId]);

  const handleAdd = (item: InventoryItem | null) => {
    if (!item) return;
    setSelected((prev) =>
      prev.some((s) => s.productCode === item.productCode)
        ? prev
        : [...prev, item]
    );
    setInputValue("");
  };

  const handleRemove = (productCode: string) => {
    setSelected((prev) => prev.filter((s) => s.productCode !== productCode));
  };

  const handleSubmit = async () => {
    if (!selected.length || !location) return;
    setSubmitting(true);
    try {
      const response = await fetchData("/products/set-bulk-location", "POST", {
        productCodes: selected.map((s) => s.productCode),
        warehouseLocation: location,
      });

      if (response.data.affected > 0) {
        toast.success(`Prodotti ubicati: ${response.data.affected}`);
        setSelected([]);
        setLocation("");
      } else {
        toast.error("Operazione fallita: " + response?.data?.message);
      }
    } catch (error: any) {
      toast.error("Operazione fallita: " + (error?.message ?? error));
      console.error("Error submitting bulk relocation:", error);
    }
    setSubmitting(false);
  };

  const availableOptions = options.filter(
    (o) => o.quantity > 0 && !selected.some((s) => s.productCode === o.productCode)
  );

  return (
    <PageContainer>
      <Box sx={{ maxWidth: 560, mx: "auto", width: "100%", p: 2 }}>
        <Typography variant="h6" mb={1} align="center">
          Ricollocazione massiva
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3} align="center">
          Cerca i prodotti in giacenza, accodali e assegna a tutti la stessa
          ubicazione.
        </Typography>

        <Autocomplete<InventoryItem>
          options={availableOptions}
          loading={loadingOptions}
          value={null}
          inputValue={inputValue}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.productCode}
          isOptionEqualToValue={(o, v) => o.productCode === v.productCode}
          noOptionsText="Nessun prodotto in giacenza"
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") setInputValue(value);
          }}
          onChange={(_, value) => handleAdd(value)}
          renderOption={(props, option) => (
            <li {...props} key={option.productCode}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <span>{option.productCode}</span>
                <Typography variant="caption" color="text.secondary">
                  giacenza: {option.quantity}
                </Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label="Cerca prodotto in giacenza"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptions ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 3, mb: 1 }}
        >
          <Typography variant="subtitle2">In canna</Typography>
          <Chip label={`${selected.length} prodotti`} size="small" />
        </Stack>
        <Divider />

        <List dense sx={{ maxHeight: 320, overflow: "auto" }}>
          {selected.length === 0 ? (
            <ListItem>
              <ListItemText
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                primary="Nessun prodotto in canna"
              />
            </ListItem>
          ) : (
            selected.map((item) => (
              <ListItem
                key={item.productCode}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="rimuovi"
                    onClick={() => handleRemove(item.productCode)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={item.productCode}
                  secondary={`giacenza: ${item.quantity}`}
                />
              </ListItem>
            ))
          )}
        </List>

        <Divider sx={{ mb: 3 }} />

        <TextField
          fullWidth
          label="Ubicazione"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!selected.length || !location || submitting}
          onClick={handleSubmit}
          startIcon={
            submitting ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          Ubica tutti ({selected.length})
        </Button>
      </Box>
    </PageContainer>
  );
};

export default Relocate;
